import {
  HORIZON_CRITERIA, HORIZON_KEYS, HORIZON_LENSES, HORIZON_META,
  horizonMetrics, horizonPoint, horizontesReady,
  type HorizonInitiative, type HorizonKey, type HorizonReading, type HorizonSuggestionDto, type Horizontes as HorizontesContent,
} from '@ads/shared';
import { useEffect, useMemo, useState, type CSSProperties, type FocusEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { useConsolidate, useEvidence, useHorizonSuggest, useMaterialAction, useToolReading } from '../lib/queries';
import { useToolDraft } from '../lib/useToolDraft';
import { EvidenceModal, type EvidenceTarget } from './EvidenceModal';
import { ReadingStatus } from './GapsDashboard';

/* ─────────────────────────────────────────────────────────────────────
   3 Horizontes de Inovação — página única.
   As iniciativas de cada horizonte (nome, descrição, evidências), com a
   sugestão da IA a partir das ferramentas anteriores; ao lado, a leitura
   visual: o plano mercados × ativos com um ponto por iniciativa, que a IA
   posiciona (e avisa quando uma parece de outro horizonte), os critérios,
   a leitura rápida e o alinhamento ao propósito.
   ───────────────────────────────────────────────────────────────────── */

const MAX = 12;
const H_ICON: Record<HorizonKey, (p: object) => ReactNode> = { h1: Ic.Target, h2: Ic.Nodes, h3: Ic.Bulb };
const LENS_ICON = { mercado: Ic.Users, capacidades: Ic.Gear, ambicao: Ic.Bars } as const;
const newId = () => `hz-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const focusName = (id: string) => requestAnimationFrame(() => {
  const el = document.querySelector<HTMLTextAreaElement>(`[data-hz="${id}"] .hz-name`);
  el?.focus();
  el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
});
type Sug = { data: HorizonSuggestionDto | null; seen: string[] };

export function Horizontes() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', '3 Horizontes de Inovação'], j: .54 });
  const nav = useNavigate();
  const { local, commit, flush, saveState, status } = useToolDraft('horizontes');
  const reading = useToolReading('horizontes');
  const suggest = useHorizonSuggest();
  const evidence = useEvidence();
  const act = useMaterialAction();
  const consolidate = useConsolidate();
  const [modal, setModal] = useState<EvidenceTarget | null>(null);
  const [sug, setSug] = useState<Partial<Record<HorizonKey, Sug>>>({});
  const [asking, setAsking] = useState<string | null>(null);
  const [big, setBig] = useState(false);

  const c = local;
  const m = useMemo(() => (c ? horizonMetrics(c) : null), [c]);
  const r = reading.data;
  const rd = r?.reading ?? null;
  const busy = !!r && (r.pending || r.stale);
  const files = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of evidence.data ?? []) if (e.ref?.startsWith('horizontes:')) map.set(e.ref.slice(11), (map.get(e.ref.slice(11)) ?? 0) + 1);
    return map;
  }, [evidence.data]);

  const set = (h: HorizonKey, initiatives: HorizonInitiative[]) => { if (c) commit({ ...c, [h]: { initiatives } }); };
  const patch = (h: HorizonKey, id: string, p: Partial<HorizonInitiative>) =>
    c && set(h, c[h].initiatives.map(i => (i.id === id ? { ...i, ...p, ai: p.ai ?? false } : i)));
  const add = (h: HorizonKey, init: Partial<HorizonInitiative> = {}) => {
    if (!c || c[h].initiatives.length >= MAX) return;
    const id = newId();
    set(h, [...c[h].initiatives, { id, name: '', description: '', ai: false, ...init }]);
    focusName(id);
  };
  const remove = (h: HorizonKey, id: string) => {
    if (!c) return;
    for (const e of evidence.data ?? []) if (e.ref === `horizontes:${id}`) act.mutate({ id: e.id, action: 'delete' });
    set(h, c[h].initiatives.filter(i => i.id !== id));
  };
  /** A IA leria a iniciativa em outro horizonte: a pessoa decide mover. */
  const move = (from: HorizonKey, id: string, to: HorizonKey) => {
    if (!c || c[to].initiatives.length >= MAX) return;
    const it = c[from].initiatives.find(i => i.id === id);
    if (!it) return;
    commit({ ...c, [from]: { initiatives: c[from].initiatives.filter(i => i.id !== id) }, [to]: { initiatives: [...c[to].initiatives, it] } });
    focusName(id);
  };
  /** Sair de um cartão deixado em branco o tira da lista. */
  const leave = (h: HorizonKey, id: string) => (e: FocusEvent<HTMLElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    const it = c?.[h].initiatives.find(i => i.id === id);
    if (it && !it.name.trim() && !it.description.trim() && !files.get(id)) remove(h, id);
  };
  const dropBlank = () => {
    if (!c) return;
    const blank = (i: HorizonInitiative) => !i.name.trim() && !i.description.trim() && !files.get(i.id!);
    if (HORIZON_KEYS.some(h => c[h].initiatives.some(blank))) commit(Object.fromEntries(HORIZON_KEYS.map(h => [h, { initiatives: c[h].initiatives.filter(i => !blank(i)) }])) as HorizontesContent);
  };

  /* a sugestão da IA: uma por vez, por horizonte; "outra" não repete as já vistas */
  const ask = (h: HorizonKey, again = false) => {
    const prev = sug[h];
    const seen = [...(prev?.seen ?? []), ...(again && prev?.data ? [prev.data.name] : [])].slice(-20);
    setSug(s => ({ ...s, [h]: { data: again ? null : prev?.data ?? null, seen } }));
    setAsking(h);
    suggest.mutate({ horizon: h, avoid: seen }, {
      onSuccess: data => setSug(s => ({ ...s, [h]: { data, seen } })),
      onSettled: () => setAsking(a => (a === h ? null : a)),
    });
  };
  const accept = (h: HorizonKey) => {
    const d = sug[h]?.data;
    if (!d) return;
    add(h, { name: d.name, description: d.description, ai: true });
    setSug(s => ({ ...s, [h]: { data: null, seen: [...(s[h]?.seen ?? []), d.name] } }));
  };
  const describe = (h: HorizonKey, it: HorizonInitiative) => {
    setAsking(it.id!);
    suggest.mutate({ horizon: h, name: it.name }, {
      onSuccess: d => { const cur = c?.[h].initiatives.find(i => i.id === it.id); if (cur && !cur.description.trim()) patch(h, it.id!, { description: d.description, ai: true }); },
      onSettled: () => setAsking(a => (a === it.id ? null : a)),
    });
  };

  const leaveTo = (to: string) => { dropBlank(); flush(); nav(to); };
  const finish = () => { dropBlank(); flush(); consolidate.mutate(undefined, { onSettled: () => nav('/strategy/w5h2') }); };
  const misplaced = new Map((rd?.deslocadas ?? []).map(d => [d.id, d]));

  return (
    <section className="view on" data-v="horizontes">
      <button className="back" type="button" onClick={() => leaveTo('/strategy')}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 09 / 10</b></div>
          <h1>3 Horizontes de Inovação</h1>
          <p className="lede">Organize as iniciativas entre os três horizontes, com clareza sobre mercado, capacidades e ambição estratégica.</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
        </div>
      </div>

      <div className="bmc-top">
        <p className="hz-sum">{m ? <>
          <b>{m.total}</b> {m.total === 1 ? 'iniciativa' : 'iniciativas'}
          {HORIZON_KEYS.map(h => <span key={h} className={`hz-pill h-${h}`}>{HORIZON_META[h].code} · {m.counts[h]}</span>)}
        </> : null}</p>
        <div className="bmc-align" title={rd?.leitura}>
          <span>Alinhamento ao propósito</span>
          <span className={`vi-bar${busy ? ' is-dim' : ''}`}><i style={{ '--v': `${rd?.alinhamento ?? 0}%` } as CSSProperties} /></span>
          <b>{rd ? `${rd.alinhamento}%` : '—'}</b>
        </div>
      </div>

      {!c || !m ? <div className="loading-line" /> : (
        <div className="hz-grid">
          <div className="hz-main">
            <section className="card hz-lens">
              <h3 className="in-h3">Como ler os cenários</h3>
              <div className="hz-lens-l">
                {HORIZON_LENSES.map(l => {
                  const LI = LENS_ICON[l.key];
                  return <div key={l.key}><span className="hz-lens-i"><LI /></span><div><b>{l.label}</b><span>{l.desc}</span></div></div>;
                })}
              </div>
            </section>

            <div className="hz-cols">
              {HORIZON_KEYS.map(h => {
                const meta = HORIZON_META[h];
                const HI = H_ICON[h];
                const list = c[h].initiatives;
                const s = sug[h];
                const full = list.length >= MAX;
                return (
                  <section key={h} className={`card hz-col h-${h}`} aria-label={`${meta.code} — ${meta.label}`}>
                    <header className="hz-col-h">
                      <div><b className="hz-code">{meta.code}</b><span className="hz-label">{meta.label}</span><span className="hz-subl">{meta.sub}</span></div>
                      <span className="hz-hi"><HI /></span>
                    </header>
                    <ul className="hz-bul">{meta.bullets.map(b => <li key={b}>{b}</li>)}</ul>

                    <div className="hz-list">
                      {list.map(it => {
                        const n = files.get(it.id!) ?? 0;
                        const off = misplaced.get(it.id!);
                        return (
                          <Card key={it.id} h={h} it={it} files={n} off={off && off.horizonte !== h ? off : undefined} describing={asking === it.id}
                            onPatch={p => patch(h, it.id!, p)} onRemove={() => remove(h, it.id!)} onBlur={leave(h, it.id!)}
                            onDescribe={() => describe(h, it)} onMove={to => move(h, it.id!, to)}
                            onEvidence={() => setModal({
                              ref: `horizontes:${it.id}`, title: it.name || 'Iniciativa', kicker: `Evidências · ${meta.code} ${meta.label}`,
                              lede: 'Documentos que mostram a demanda, a oportunidade ou a capacidade por trás da iniciativa — pesquisas, dados, estudos, benchmarks. O ADS lê cada um, e a leitura visual considera o que eles mostram.',
                              empty: 'Com evidência, a leitura da IA sobre a iniciativa fica apoiada no que o documento mostra.',
                            })} />
                        );
                      })}
                    </div>

                    {(s?.data || asking === h) && (
                      <div className={`hz-sug${asking === h ? ' is-busy' : ''}`} aria-live="polite">
                        <div className="hz-sug-h"><Ic.Sparkle /><b>Sugestão da IA</b>{s?.data?.basic && <span className="obj-st">Leitura básica</span>}</div>
                        {asking === h && !s?.data ? <div className="obj-skel"><i /><i /></div> : s?.data && (
                          <>
                            <b className="hz-sug-n">{s.data.name}</b>
                            <p>{s.data.description}</p>
                            {s.data.fonte && <span className="hz-sug-f">A partir de: {s.data.fonte}</span>}
                            <div className="hz-sug-a">
                              <button type="button" className="btn-ghost sm hz-use" onClick={() => accept(h)} disabled={full}><Ic.Plus />Adicionar</button>
                              <button type="button" className="btn-ghost sm" onClick={() => ask(h, true)} disabled={asking === h}><Ic.Refresh />Outra</button>
                              <button type="button" className="va-btn" aria-label="Descartar sugestão" onClick={() => setSug(x => ({ ...x, [h]: { data: null, seen: [...(x[h]?.seen ?? []), s.data!.name] } }))}><Ic.Close /></button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="hz-add">
                      <button type="button" className="hz-add-b" onClick={() => add(h)} disabled={full}><Ic.Plus />Adicionar iniciativa</button>
                      <button type="button" className="hz-ai-b" onClick={() => ask(h, !!s?.data)} disabled={asking === h || full} title="A IA sugere uma iniciativa a partir do que as ferramentas anteriores mostraram">
                        {asking === h ? <span className="spin" /> : <Ic.Sparkle />}Sugerir com IA
                      </button>
                    </div>
                  </section>
                );
              })}
            </div>
          </div>

          <aside className="in-side hz-side">
            <section className="card hz-chart-card">
              <div className="gd-h">
                <h3 className="in-h3">Leitura visual dos horizontes</h3>
                <button type="button" className="va-btn" onClick={() => setBig(true)} aria-label="Ampliar o gráfico" title="Ampliar"><Ic.External /></button>
              </div>
              <HorizonChart c={c} reading={rd} busy={busy} onPick={id => focusName(id)} />
              <div className="hz-legend">{HORIZON_KEYS.map(h => <span key={h} className={`h-${h}`}><i />{HORIZON_META[h].code} {HORIZON_META[h].label}</span>)}</div>
              <div className="hz-chart-st">
                <ReadingStatus updating={busy} basic={r?.basic ?? false} at={r?.updatedAt ?? null} />
                {!horizontesReady(c) && <span className="gd-empty">A IA posiciona as iniciativas a partir de duas com nome.</span>}
              </div>
              {r?.note && <p className="gd-note"><Ic.Warning />{r.note}</p>}
            </section>
            <section className="card">
              <h3 className="in-h3"><Ic.Sparkle />Critérios da IA</h3>
              <ul className={`mc-crit hz-crit${busy ? ' is-dim' : ''}`}>
                {HORIZON_CRITERIA.map(([k, label]) => (
                  <li key={k}><span>{label}</span><b>{rd ? `${rd.criterios[k]}%` : '—'}</b><span className="tr"><i style={{ '--v': `${rd?.criterios[k] ?? 0}%` } as CSSProperties} /></span></li>
                ))}
              </ul>
            </section>
            <section className="card">
              <h3 className="in-h3">Leitura rápida</h3>
              <ul className="hz-quick">
                {HORIZON_KEYS.map(h => (
                  <li key={h} className={`h-${h}`}><span className="hz-chip">{HORIZON_META[h].code}</span><b>{HORIZON_META[h].quick}</b><span>{HORIZON_META[h].outcome}</span><em>{m.counts[h]}</em></li>
                ))}
              </ul>
              {rd && <p className={`hz-read${busy ? ' is-dim' : ''}`}>{rd.leitura}</p>}
            </section>
          </aside>
        </div>
      )}

      {big && c && createPortal(
        <div className="hz-scrim" onMouseDown={e => { if (e.target === e.currentTarget) setBig(false); }} onKeyDown={e => { if (e.key === 'Escape') setBig(false); }}>
          <div className="hz-big" role="dialog" aria-modal="true" aria-label="Leitura visual dos horizontes">
            <div className="gd-h"><h3 className="in-h3">Leitura visual dos horizontes</h3><button type="button" className="pub-x" onClick={() => setBig(false)} aria-label="Fechar" autoFocus><Ic.Close /></button></div>
            <HorizonChart c={c} reading={rd} busy={busy} labels onPick={id => { setBig(false); focusName(id); }} />
            <div className="hz-legend">{HORIZON_KEYS.map(h => <span key={h} className={`h-${h}`}><i />{HORIZON_META[h].code} {HORIZON_META[h].label}</span>)}</div>
          </div>
        </div>, document.body)}
      {modal && <EvidenceModal target={modal} onClose={() => setModal(null)} />}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => leaveTo('/strategy')}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => leaveTo('/strategy/macro')}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending} onClick={finish}>
          {consolidate.isPending ? 'Consolidando o DNA…' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}

/* ── peças ────────────────────────────────────────────────────────── */

function Card({ h, it, files, off, describing, onPatch, onRemove, onBlur, onDescribe, onMove, onEvidence }: {
  h: HorizonKey; it: HorizonInitiative; files: number; off?: HorizonReading['deslocadas'][number]; describing: boolean;
  onPatch: (p: Partial<HorizonInitiative>) => void; onRemove: () => void; onBlur: (e: FocusEvent<HTMLElement>) => void;
  onDescribe: () => void; onMove: (to: HorizonKey) => void; onEvidence: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (!confirm) return; const t = setTimeout(() => setConfirm(false), 3500); return () => clearTimeout(t); }, [confirm]);
  return (
    <article data-hz={it.id} className={`hz-card${it.ai ? ' ai' : ''}`} onBlur={onBlur}>
      <label className="hz-fld"><span>Iniciativa / fluxo prioritário{it.ai && <em className="hz-ai" title="Sugerida pela IA — edite à vontade"><Ic.Sparkle />IA</em>}</span>
        <textarea className="hz-name" rows={1} value={it.name} maxLength={120} placeholder="Nome da iniciativa"
          onChange={e => onPatch({ name: e.target.value.replace(/\n/g, ' ') })}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); (e.currentTarget.closest('.hz-card')?.querySelector('.hz-desc') as HTMLTextAreaElement | null)?.focus(); } }} />
      </label>
      <label className="hz-fld"><span>Descreva a iniciativa</span>
        <textarea className="hz-desc" value={it.description} maxLength={600} rows={2} placeholder="O que é, para quem e o que muda." onChange={e => onPatch({ description: e.target.value })} />
      </label>
      {it.name.trim() && !it.description.trim() && (
        <button type="button" className="hz-desc-ai" onClick={onDescribe} disabled={describing}>{describing ? <span className="spin" /> : <Ic.Sparkle />}Descrever com IA</button>
      )}
      {off && (
        <div className="hz-off">
          <Ic.Bulb /><p>A IA leria esta iniciativa em <b>{HORIZON_META[off.horizonte].code}</b>{off.motivo ? ` — ${off.motivo.replace(/^./, x => x.toLowerCase())}` : '.'}</p>
          <button type="button" className="btn-ghost sm" onClick={() => onMove(off.horizonte)}>Mover para {HORIZON_META[off.horizonte].code}</button>
        </div>
      )}
      <div className="hz-card-a">
        <button type="button" className={`evid g-evid${files ? '' : ' none'}`} onClick={onEvidence}><Ic.Clip />{files ? `${files} ${files > 1 ? 'evidências' : 'evidência'}` : 'Anexar evidência'}</button>
        {confirm
          ? <button type="button" className="g-del" onClick={onRemove}>Excluir</button>
          : <button type="button" className="va-btn in-x" onClick={() => setConfirm(true)} aria-label={`Excluir a iniciativa${it.name ? ` ${it.name}` : ''}`}><Ic.Close /></button>}
      </div>
      <span className="hz-card-h" aria-hidden="true">{HORIZON_META[h].code}</span>
    </article>
  );
}

/* o plano: ativos (x) × mercados (y); 0–100 nos dois eixos. Ampliado, o desenho cresce e o texto não. */
const GEO = {
  small: { W: 300, H: 250, X0: 54, X1: 290, Y0: 12, Y1: 200, dot: 3.6, ring: 6.5 },
  big: { W: 680, H: 460, X0: 92, X1: 640, Y0: 18, Y1: 392, dot: 5, ring: 8.5 },
};

export function HorizonChart({ c, reading, busy, labels, onPick }: { c: HorizontesContent; reading: HorizonReading | null; busy: boolean; labels?: boolean; onPick: (id: string) => void }) {
  const g = labels ? GEO.big : GEO.small;
  const { W, H, X0, X1, Y0, Y1 } = g;
  const k = (X1 - X0) / 236;
  const px = (ativos: number) => X0 + (ativos / 100) * (X1 - X0);
  const py = (mercado: number) => Y1 - (mercado / 100) * (Y1 - Y0);
  const off = new Set((reading?.deslocadas ?? []).map(d => d.id));
  const pts = HORIZON_KEYS.flatMap(h => c[h].initiatives.filter(i => i.name.trim()).map((i, n) => ({ h, i, p: horizonPoint(h, n, i.id, reading) })));
  const count = (h: HorizonKey) => pts.filter(x => x.h === h).length;
  /* os nomes (ampliado): à direita do ponto, ou à esquerda perto da borda; sem encavalar um no outro */
  const placed: Array<{ a: number; b: number; y: number }> = [];
  const tags = labels ? [...pts].sort((a, b) => py(a.p.mercado) - py(b.p.mercado)).map(({ i, p }) => {
    const x = px(p.ativos);
    const end = x > X0 + (X1 - X0) * .62;
    const name = i.name.length > 34 ? `${i.name.slice(0, 33)}…` : i.name;
    const tx = end ? x - g.dot - 5 : x + g.dot + 5;
    const w = name.length * 6.4;
    const [a, b] = end ? [tx - w, tx] : [tx, tx + w];
    /* desce enquanto cruzar um nome já posto (dos dois lados do ponto) */
    let y = py(p.mercado) + 4;
    for (let k = 0; k < placed.length; k++) {
      const hit = placed.find(q => q.a < b + 6 && a < q.b + 6 && Math.abs(q.y - y) < 15);
      if (!hit) break;
      y = hit.y + 15;
    }
    placed.push({ a, b, y });
    return { id: i.id!, x: tx, y, end, name };
  }) : [];
  return (
    <svg className={`hz-chart${labels ? ' big' : ''}${busy ? ' is-busy' : ''}`} viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label={`Gráfico dos horizontes: ${pts.map(x => `${x.i.name} (${HORIZON_META[x.h].code})`).join('; ') || 'sem iniciativas'}`}>
      <defs><marker id={labels ? 'hzArrowB' : 'hzArrow'} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 8 4 0 8z" className="hz-ax-h" /></marker></defs>
      {HORIZON_KEYS.map(h => {
        const cc = HORIZON_META[h].center;
        const r = Math.min(50, 28 + count(h) * 3.2) * k;
        const cx = px(cc.ativos), cy = py(cc.mercado);
        /* o rótulo do horizonte vai para o lado da bolha mais livre dos pontos */
        const mine = [
          ...pts.filter(x => x.h === h).map(x => [px(x.p.ativos), py(x.p.mercado)] as const),
          /* ampliado: os nomes das iniciativas também ocupam lugar */
          ...tags.flatMap(t => Array.from({ length: Math.ceil(t.name.length * 6.4 / 12) + 1 }, (_, j) => [t.end ? t.x - j * 12 : t.x + j * 12, t.y - 4] as const)),
        ];
        const spots = [[0, .55], [0, -.4], [-.5, .1], [.5, .1], [-.36, .5], [.36, .5]].map(([dx, dy]) => [cx + dx! * r, cy + dy! * r] as const);
        const room = (sx: number, sy: number) => Math.min(Infinity, ...mine.map(([x, y]) => Math.hypot(Math.max(0, Math.abs(x - sx) - 9 * k), y - (sy - 6 * k))));
        const [lx, ly] = spots.reduce((best, sp) => (room(sp[0], sp[1]) > room(best[0], best[1]) + 2 ? sp : best), spots[0]!);
        return (
          <g key={h} className={`hz-b h-${h}`}>
            <circle cx={cx} cy={cy} r={r} />
            <text x={lx} y={ly} textAnchor="middle">{HORIZON_META[h].code}</text>
          </g>
        );
      })}
      <line x1={X0} y1={Y1} x2={X0} y2={Y0 - 4} className="hz-ax" markerEnd={`url(#${labels ? 'hzArrowB' : 'hzArrow'})`} />
      <line x1={X0} y1={Y1} x2={X1 + 6} y2={Y1} className="hz-ax" markerEnd={`url(#${labels ? 'hzArrowB' : 'hzArrow'})`} />
      <text x={X0 - 8} y={Y0 + 10} textAnchor="end" className="hz-tick">Novos</text>
      <text x={X0 - 8} y={Y0 + 22} textAnchor="end" className="hz-tick">mercados</text>
      <text x={X0 - 8} y={Y1 - 14} textAnchor="end" className="hz-tick">Mercados</text>
      <text x={X0 - 8} y={Y1 - 2} textAnchor="end" className="hz-tick">atuais</text>
      <text x={X0 + 2} y={Y1 + 16} className="hz-tick">Ativos atuais</text>
      <text x={X1} y={Y1 + 16} textAnchor="end" className="hz-tick">Novos ativos</text>
      <text x={(X0 + X1) / 2} y={H - 5} textAnchor="middle" className="hz-axt">Produtos &amp; ativos</text>
      <text x={12} y={(Y0 + Y1) / 2} textAnchor="middle" className="hz-axt" transform={`rotate(-90 12 ${(Y0 + Y1) / 2})`}>Mercados &amp; consumidores</text>
      {pts.map(({ h, i, p }) => (
        <g key={i.id} className={`hz-dot h-${h}${off.has(i.id!) ? ' off' : ''}${p.read ? '' : ' unread'}`}
          style={{ transform: `translate(${px(p.ativos)}px, ${py(p.mercado)}px)` }}
          tabIndex={0} role="button" aria-label={`${i.name} — ${HORIZON_META[h].code}`}
          onClick={() => onPick(i.id!)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(i.id!); } }}>
          <title>{`${i.name} · ${HORIZON_META[h].code}${off.has(i.id!) ? ' · a IA leria em outro horizonte' : ''}`}</title>
          {off.has(i.id!) && <circle r={g.ring} className="ring" />}
          <circle r={g.dot} />
        </g>
      ))}
      {tags.map(t => <text key={t.id} x={t.x} y={t.y} textAnchor={t.end ? 'end' : 'start'} className="hz-dl">{t.name}</text>)}
    </svg>
  );
}
